import { observer } from 'mobx-react'
import React, { useEffect, useRef, useState } from 'react'
import { useStore } from '../../../app/data/root.store'
import { SmartTagIcon } from '../VaultMetadataPanel/MetadataLabelsSection/TagsTagCategory/SmartTagIcon'
import { ScrollableFiltersContainer, ScrollArrow, VaultFiltersWrapper } from './VaultFilters.styles'
import { FilterButton } from './components/FilterButton'
import { PREDEFINED_TAG_NAMES } from '../../data/vault-store'

export const VaultFilters = observer(() => {
    const { vaultStore } = useStore()
    const [showLeftArrow, setShowLeftArrow] = useState(false)
    const [showRightArrow, setShowRightArrow] = useState(false)
    const scrollContainerRef = useRef(null)

    const checkScrollButtons = () => {
        const container = scrollContainerRef.current
        if (!container) return

        const { scrollLeft, scrollWidth, clientWidth } = container;

        setShowLeftArrow(scrollLeft > 0)
        setShowRightArrow(scrollLeft < (scrollWidth - clientWidth - 44))
    }

    const scrollLeft = () => {
        const container = scrollContainerRef.current
        if (container) {
            container.scrollBy({ left: -200, behavior: 'smooth' })
        }
    }

    const scrollRight = () => {
        const container = scrollContainerRef.current
        if (container) {
            container.scrollBy({ left: 200, behavior: 'smooth' })
        }
    }

    useEffect(() => {
        const container = scrollContainerRef.current
        if (container) {
            checkScrollButtons()
            container.addEventListener('scroll', checkScrollButtons)
            window.addEventListener('resize', checkScrollButtons)

            return () => {
                container.removeEventListener('scroll', checkScrollButtons)
                window.removeEventListener('resize', checkScrollButtons)
            }
        }
    }, [])

    const getTagIcon = () => {
        const firstTag = vaultStore.sortedSelectedTags[0]
        if (firstTag) {
            return <SmartTagIcon tagName={firstTag.name || firstTag.id} />
        }
        const hasSelection = vaultStore.hasSelectedTags;
        return <DynamicFolderIcon inverted={hasSelection} />;
    }

    const getPeriodIcon = () => {
        const hasSelection = vaultStore.hasSelectedPeriods;
        return <DynamicCalendarIcon inverted={hasSelection} />;
    }

    const getStatusIcon = () => {
        const hasSelection = vaultStore.hasSelectedStatuses;
        return <DynamicStatusIcon inverted={hasSelection} />;
    }

    const getStatusLabel = (statusId) => {
        const statusMap = {
            'Processing': 'In progress',
            'Done': 'Completed'
        };
        return statusMap[statusId] || statusId;
    };

    const getFilterLabel = (items, label, filterType) => {
        if (!items || items.length === 0) return label

        if (filterType === 'period') {
            if (items.length === 1) {
                return items[0].label || items[0].name || label
            }
            return `${label} (${items.length})`
        }

        if (filterType === 'status') {
            if (items.length === 1) {
                return items[0].label || getStatusLabel(items[0].id) || label
            }
            return `${label} (${items.length})`
        }

        if (filterType === 'tags') {
            if (items.length === 1) {
                return PREDEFINED_TAG_NAMES[items[0].label] || items[0].label || items[0].name;
            }
            return `${label} (${items.length})`
        }

        if (items.length === 1) {
            return items[0].label || items[0].name || items[0].tag || label
        }
        return `${label} (${items.length})`
    }

    const handleRemoveFilter = (filterId) => {
        vaultStore.removeFilter(filterId)
    }

    const handleTagSelection = (tag) => {
        vaultStore.toggleTag(tag)
    }

    const handleStatusSelection = (statusId, statusLabel) => {
        vaultStore.toggleStatus(statusId, statusLabel)
    }

    const handlePeriodSelect = (period) => {
        vaultStore.togglePeriod(period)
    }

    const filterConfig = [
        ...(vaultStore.isAssetVaultTab ? [] : [{
            id: 'allFiles',
            label: 'All files',
            count: vaultStore.meta.totalVaultsInDB,
            hasSelection: vaultStore.isAllFilesFilterActive,
            showRemoveButton: false,
            type: 'button',
            onClickButton: () => vaultStore.resetAndRefetchFiles()
        }]),
        {
            id: 'tags',
            label: getFilterLabel(vaultStore.sortedSelectedTags, 'Tags', 'tags'),
            icon: getTagIcon(),
            type: 'dropdown',
            hasSelection: vaultStore.hasSelectedTags,
            onDataFetch: () => vaultStore.fetchUserTags(),
            selectedItems: vaultStore.selectedTags,
            onItemSelect: handleTagSelection
        },
        {
            id: 'period',
            label: getFilterLabel(vaultStore.aggregatedSelectedPeriods, 'Period', 'period'),
            icon: getPeriodIcon(),
            type: 'dropdown',
            hasSelection: vaultStore.hasSelectedPeriods,
            onDataFetch: () => vaultStore.fetchUserPeriods(),
            selectedItems: vaultStore.selectedPeriods,
            onItemSelect: handlePeriodSelect
        },
        {
            id: 'status',
            label: getFilterLabel(vaultStore.selectedStatuses, 'Status', 'status'),
            icon: getStatusIcon(),
            type: 'dropdown',
            hasSelection: vaultStore.hasSelectedStatuses,
            selectedItems: vaultStore.selectedStatuses,
            onItemSelect: handleStatusSelection
        }
    ]

    const isEmptyVault = vaultStore.files.length === 0 && !vaultStore.isLoading && !vaultStore.isRefetchPending && !vaultStore.hasActiveFilters;

    return (
        <VaultFiltersWrapper style={isEmptyVault ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
            {showLeftArrow && !isEmptyVault && (
                <ScrollArrow direction="left" onClick={scrollLeft}>
                    ‹
                </ScrollArrow>
            )}

            <ScrollableFiltersContainer ref={scrollContainerRef}>
                {filterConfig.map(filter => (
                    <FilterButton
                        key={filter.id}
                        filter={filter}
                        isActive={(!isEmptyVault || filter.id === 'allFiles') && (vaultStore.activeFilters.includes(filter.id) || filter.hasSelection)}
                        hasDropdown={!isEmptyVault && filter.type === 'dropdown'}
                        onRemove={!isEmptyVault && filter.id && (false !== filter.showRemoveButton) && (filter.type === 'removable' || filter.hasSelection) ? handleRemoveFilter : null}
                        disabled={isEmptyVault}
                        onClickButton={filter.onClickButton}
                    />
                ))}
            </ScrollableFiltersContainer>

            {showRightArrow && !isEmptyVault && (
                <ScrollArrow direction="right" onClick={scrollRight}>
                    ›
                </ScrollArrow>
            )}
        </VaultFiltersWrapper>
    )
})

const DynamicStatusIcon = ({ inverted = false }) => {
    const color = inverted ? '#FFFFFF' : '#000000';
    const opacity1 = inverted ? '0.5' : '0.3';
    const opacity2 = inverted ? '0.7' : '0.5';

    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g opacity={opacity1}>
                <path d="M7.25 11C7.66421 11 8 11.3358 8 11.75C8 12.1642 7.66421 12.5 7.25 12.5H5.75C5.33579 12.5 5 12.1642 5 11.75C5 11.3358 5.33579 11 5.75 11H7.25Z" fill={color} />
                <path d="M7.75 11.75C7.75 11.4739 7.52614 11.25 7.25 11.25H5.75C5.47386 11.25 5.25 11.4739 5.25 11.75C5.25 12.0261 5.47386 12.25 5.75 12.25H7.25C7.52614 12.25 7.75 12.0261 7.75 11.75ZM8.25 11.75C8.25 12.3023 7.80228 12.75 7.25 12.75H5.75C5.19772 12.75 4.75 12.3023 4.75 11.75C4.75 11.1977 5.19772 10.75 5.75 10.75H7.25C7.80228 10.75 8.25 11.1977 8.25 11.75Z" fill={color} />
            </g>
            <path d="M18.25 11C18.6642 11 19 11.3358 19 11.75C19 12.1642 18.6642 12.5 18.25 12.5H16.75C16.3358 12.5 16 12.1642 16 11.75C16 11.3358 16.3358 11 16.75 11H18.25Z" fill={color} />
            <path d="M18.75 11.75C18.75 11.4739 18.5261 11.25 18.25 11.25H16.75C16.4739 11.25 16.25 11.4739 16.25 11.75C16.25 12.0261 16.4739 12.25 16.75 12.25H18.25C18.5261 12.25 18.75 12.0261 18.75 11.75ZM19.25 11.75C19.25 12.3023 18.8023 12.75 18.25 12.75H16.75C16.1977 12.75 15.75 12.3023 15.75 11.75C15.75 11.1977 16.1977 10.75 16.75 10.75H18.25C18.8023 10.75 19.25 11.1977 19.25 11.75Z" fill={color} />
            <path d="M11.2501 16.5005C11.2501 16.0863 11.5859 15.7505 12.0001 15.7505C12.4143 15.7505 12.7501 16.0863 12.7501 16.5005L12.7501 18.0005C12.7501 18.4147 12.4143 18.7505 12.0001 18.7505C11.5859 18.7505 11.2501 18.4147 11.2501 18.0005L11.2501 16.5005Z" fill={color} />
            <path d="M12.0001 16.0005C11.724 16.0005 11.5001 16.2243 11.5001 16.5005L11.5001 18.0005C11.5001 18.2766 11.724 18.5005 12.0001 18.5005C12.2763 18.5005 12.5001 18.2766 12.5001 18.0005L12.5001 16.5005C12.5001 16.2243 12.2763 16.0005 12.0001 16.0005ZM12.0001 15.5005C12.5524 15.5005 13.0001 15.9482 13.0001 16.5005V18.0005C13.0001 18.5528 12.5524 19.0005 12.0001 19.0005C11.4478 19.0005 11.0001 18.5528 11.0001 18.0005L11.0001 16.5005C11.0001 15.9482 11.4478 15.5005 12.0001 15.5005Z" fill={color} />
            <path d="M11.2501 5.50049C11.2501 5.08627 11.5859 4.75049 12.0001 4.75049C12.4143 4.75049 12.7501 5.08627 12.7501 5.50049L12.7501 7.00049C12.7501 7.4147 12.4143 7.75049 12.0001 7.75049C11.5859 7.75049 11.2501 7.4147 11.2501 7.00049L11.2501 5.50049Z" fill={color} />
            <path d="M12.0001 5.00049C11.724 5.00049 11.5001 5.22435 11.5001 5.50049L11.5001 7.00049C11.5001 7.27663 11.724 7.50049 12.0001 7.50049C12.2763 7.50049 12.5001 7.27663 12.5001 7.00049L12.5001 5.50049C12.5001 5.22435 12.2763 5.00049 12.0001 5.00049ZM12.0001 4.50049C12.5524 4.50049 13.0001 4.9482 13.0001 5.50049V7.00049C13.0001 7.55277 12.5524 8.00049 12.0001 8.00049C11.4478 8.00049 11.0001 7.55277 11.0001 7.00049L11.0001 5.50049C11.0001 4.9482 11.4478 4.50049 12.0001 4.50049Z" fill={color} />
            <g opacity={opacity2}>
                <path d="M8.11065 14.579C8.40355 14.2862 8.87842 14.2862 9.17131 14.579C9.46421 14.8719 9.46421 15.3468 9.17131 15.6397L8.11065 16.7004C7.81776 16.9933 7.34289 16.9933 7.04999 16.7004C6.7571 16.4075 6.7571 15.9326 7.04999 15.6397L8.11065 14.579Z" fill={color} />
                <path d="M8.99454 14.7558C8.79927 14.5606 8.48269 14.5606 8.28743 14.7558L7.22677 15.8165C7.03151 16.0117 7.03151 16.3283 7.22677 16.5236C7.42203 16.7189 7.73861 16.7189 7.93388 16.5236L8.99454 15.4629C9.1898 15.2677 9.1898 14.9511 8.99454 14.7558ZM9.34809 14.4023C9.73861 14.7928 9.73861 15.426 9.34809 15.8165L8.28743 16.8771C7.8969 17.2677 7.26374 17.2677 6.87322 16.8771C6.48269 16.4866 6.48269 15.8535 6.87322 15.4629L7.93388 14.4023C8.3244 14.0117 8.95756 14.0117 9.34809 14.4023Z" fill={color} />
            </g>
            <path d="M15.8889 6.80072C16.1817 6.50783 16.6566 6.50783 16.9495 6.80072C17.2424 7.09362 17.2424 7.56849 16.9495 7.86138L15.8889 8.92204C15.596 9.21494 15.1211 9.21494 14.8282 8.92204C14.5353 8.62915 14.5353 8.15428 14.8282 7.86138L15.8889 6.80072Z" fill={color} />
            <path d="M16.7727 6.9775C16.5775 6.78224 16.2609 6.78224 16.0656 6.9775L15.005 8.03816C14.8097 8.23342 14.8097 8.55001 15.005 8.74527C15.2002 8.94053 15.5168 8.94053 15.7121 8.74527L16.7727 7.68461C16.968 7.48935 16.968 7.17276 16.7727 6.9775ZM17.1263 6.62395C17.5168 7.01447 17.5168 7.64764 17.1263 8.03816L16.0656 9.09882C15.6751 9.48935 15.0419 9.48935 14.6514 9.09882C14.2609 8.7083 14.2609 8.07513 14.6514 7.68461L15.7121 6.62395C16.1026 6.23342 16.7358 6.23342 17.1263 6.62395Z" fill={color} />
            <path d="M14.8283 15.6387C14.5354 15.3458 14.5354 14.871 14.8283 14.5781C15.1212 14.2852 15.5961 14.2852 15.889 14.5781L16.9496 15.6387C17.2425 15.9316 17.2425 16.4065 16.9496 16.6994C16.6567 16.9923 16.1819 16.9923 15.889 16.6994L14.8283 15.6387Z" fill={color} />
            <path d="M15.0051 14.7548C14.8098 14.9501 14.8098 15.2667 15.0051 15.462L16.0657 16.5226C16.261 16.7179 16.5776 16.7179 16.7729 16.5226C16.9681 16.3273 16.9681 16.0108 16.7729 15.8155L15.7122 14.7548C15.5169 14.5596 15.2004 14.5596 15.0051 14.7548ZM14.6515 14.4013C15.0421 14.0108 15.6752 14.0108 16.0657 14.4013L17.1264 15.462C17.5169 15.8525 17.5169 16.4856 17.1264 16.8762C16.7359 17.2667 16.1027 17.2667 15.7122 16.8762L14.6515 15.8155C14.261 15.425 14.261 14.7918 14.6515 14.4013Z" fill={color} />
            <path opacity="0.3" d="M6.87334 6.62297C7.26386 6.23245 7.89703 6.23245 8.28755 6.62297L9.34821 7.68363C9.73874 8.07416 9.73874 8.70732 9.34821 9.09784C8.95769 9.48837 8.32452 9.48837 7.934 9.09784L6.87334 8.03718C6.48281 7.64666 6.48281 7.0135 6.87334 6.62297Z" fill={color} />
        </svg>
    );
};

const DynamicCalendarIcon = ({ inverted = false }) => {
    const strokeColor = inverted ? '#FFFFFF' : '#1A1B1D';
    const fillColor = inverted ? '#FFFFFF' : '#1A1B1D';

    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3.75" y="6.17822" width="16.5" height="13.9286" rx="2.25" stroke={strokeColor} strokeWidth="1.5" />
            <path d="M21 10.5713H3" stroke={strokeColor} strokeWidth="1.5" />
            <path d="M8.89282 4.14244C8.89282 3.72823 8.55704 3.39244 8.14282 3.39244C7.72861 3.39244 7.39282 3.72822 7.39282 4.14244L8.89282 4.14244ZM8.14282 4.14244L7.39282 4.14244L7.39282 6.71387L8.14282 6.71387L8.89282 6.71387L8.89282 4.14244L8.14282 4.14244Z" fill={fillColor} />
            <path d="M16.6072 4.14244C16.6072 3.72823 16.2714 3.39244 15.8572 3.39244C15.443 3.39244 15.1072 3.72822 15.1072 4.14244L16.6072 4.14244ZM15.8572 4.14244L15.1072 4.14244L15.1072 6.71387L15.8572 6.71387L16.6072 6.71387L16.6072 4.14244L15.8572 4.14244Z" fill={fillColor} />
        </svg>
    );
};

const DynamicFolderIcon = ({ inverted = false }) => {
    const strokeColor = inverted ? '#FFFFFF' : '#1A1B1D';
    const fillColor = inverted ? '#000000' : 'white';

    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.75 18.5L18.75 8.64258C18.7499 8.15956 18.5945 7.69123 18.3096 7.30566L18.1797 7.14551L15.3779 4.00293C14.951 3.52402 14.3398 3.25001 13.6982 3.25L7.5 3.25C6.25736 3.25 5.25 4.25736 5.25 5.5L5.25 18.5C5.25 19.7426 6.25736 20.75 7.5 20.75L16.5 20.75C17.7426 20.75 18.75 19.7426 18.75 18.5Z" fill={fillColor} stroke={strokeColor} strokeWidth="1.5" />
            <path d="M18.4287 8.83398L14.0716 8.83398C13.5193 8.83398 13.0716 8.38627 13.0716 7.83398L13.0716 3.55621" stroke={strokeColor} strokeWidth="1.5" />
        </svg>
    );
};